import type { IdNameResult } from '@/api/useMetadata/types'
import DiagonalDashDark from '@/assets/Misc/diag_dash_dark.svg'
import DiagonalDashLight from '@/assets/Misc/diag_dash_light.svg'
import type { InferResponse } from '@/hooks/useApiClient'
import { formatters } from '@/utils/intl/dateTimeFormatters'
import { MoonFilled, SunFilled } from '@ant-design/icons'
import { Horizontal, Texto, Vertical, useThemeContext } from '@gravitate-js/excalibrr'
import { Select } from 'antd'
import dayjs from 'dayjs'
import type { MappedScheduleItem } from '../Context/ScheduleItems/scheduleItemStore'
import type { ConflictError } from '../utils/ConflictValidators'

export type EquipmentOverrideCardProps = {
  date: Date | undefined
  item: InferResponse<'/driver_schedule/template/overview'>[number]['days'][number]
  tractorId: string | null | undefined
  trailerId: string | null | undefined
  onTractorChange: (value: string) => void
  onTrailerChange: (value: string) => void
  tractorMetadata?: IdNameResult[]
  trailerMetadata?: IdNameResult[]
  conflict?: {
    start: dayjs.Dayjs | null
    errors: ConflictError<MappedScheduleItem>[]
  }
}
export function EquipmentOverrideCard({
  item,
  date,
  tractorId,
  trailerId,
  onTractorChange,
  onTrailerChange,
  tractorMetadata = [],
  trailerMetadata = [],
  conflict,
}: EquipmentOverrideCardProps) {
  const theme = useThemeContext()

  return (
    <Vertical
      className={`bordered p-${item.shift ? 3 : 0}`}
      style={{
        backgroundColor: conflict && item.shift ? 'var(--theme-error-dim)' : 'var(--gray-100)',
        borderRadius: 4,
        border: `1px solid ${conflict && item.shift ? 'var(--theme-error)' : 'var(--gray-200)'}`,
      }}
    >
      {item.shift ? (
        <>
          <Horizontal
            style={{
              backgroundColor: item.shift === 'am' ? 'var(--theme-optimal-dim)' : 'var(--theme-color-1-dim)',
              borderLeft: item.shift === 'am' ? '6px solid var(--theme-optimal)' : '4px solid var(--theme-color-1)',
              border: `1px solid ${item.shift === 'am' ? 'var(--theme-optimal)' : 'var(--theme-color-1)'}`,
              borderRadius: 4,
              paddingLeft: 8,
              paddingRight: 8,
              paddingTop: 4,
              paddingBottom: 4,
              gap: 8,
              alignItems: 'center',
              marginTop: 4,
            }}
          >
            <Horizontal
              gap={8}
              alignItems='center'
              width='100%'
              style={{ color: item.shift === 'am' ? 'var(--theme-optimal)' : 'var(--theme-color-1)' }}
            >
              {item.shift === 'am' ? <SunFilled /> : <MoonFilled />}
              <Horizontal flex={1} justifyContent='space-between'>
                <Texto weight='bold' appearance={item.shift === 'am' ? 'warning' : 'secondary'}>
                  {`${item.shift?.toUpperCase()} Shift`}
                </Texto>
                <Texto weight='bold' appearance={item.shift === 'am' ? 'warning' : 'secondary'}>
                  {`${dayjs(date).format('ddd')} - ${formatters.DATE(date!)}`}
                </Texto>
              </Horizontal>
            </Horizontal>
          </Horizontal>
          <Horizontal gap={8} style={{ marginTop: 8 }}>
            <Vertical flex={1}>
              <Texto appearance='medium'>Tractor</Texto>
              <Select
                showSearch
                optionFilterProp='label'
                value={tractorId}
                onChange={onTractorChange}
                className='flex-1'
                options={tractorMetadata?.map((x) => ({ value: x.id, label: x.name }))}
              />
            </Vertical>
            <Vertical flex={1}>
              <Texto appearance='medium'>Trailer</Texto>
              <Select
                showSearch
                optionFilterProp='label'
                value={trailerId}
                onChange={onTrailerChange}
                className='flex-1'
                options={trailerMetadata?.map((x) => ({ value: x.id, label: x.name }))}
              />
            </Vertical>
          </Horizontal>
          {conflict && (
            <Horizontal style={{ marginTop: 8 }} alignItems='center' gap={8}>
              <Texto appearance='error'>
                <b>* Conflict: </b>
                On {formatters.DATE(conflict.start!)} {conflict.errors[0].value}
              </Texto>
            </Horizontal>
          )}
        </>
      ) : (
        <Horizontal
          justifyContent='center'
          alignItems='center'
          style={{
            minHeight: 100,
            backgroundImage: `url("${theme.isDarkTheme ? DiagonalDashDark : DiagonalDashLight}")`,
            backgroundRepeat: 'repeat',
            backgroundSize: '18px 18px',
          }}
        >
          <Texto weight='bolder' appearance='light' category='p1' style={{ backgroundColor: 'var(--bg-theme-1)' }}>
            No shift assigned for {date && formatters.DATE(date)}
          </Texto>
        </Horizontal>
      )}
    </Vertical>
  )
}
